import { defineStore } from 'pinia';
import { ref } from 'vue';
import opportunitiesAPI from '@/api/opportunities';
import type { Opportunity } from '@/types/Opportunity';

export const useOpportunitiesStore = defineStore('opportunities', () => {
  const opportunities = ref<Opportunity[]>([]);
  
  const fetchOpportunities = async (params: any): Promise<{ data: Opportunity[]; total: number }> => {
    try {
      
      const response = await opportunitiesAPI.getOpportunities(params);
      const data: Opportunity[] = response.data.data;
      const total = response.data.meta.total;
      opportunities.value = data;
      return { data, total };
    } catch (error) {
      console.error('Error fetching opportunities:', error);
      throw error;
    }
  };

  const fetchOpportunityById = async (id: string) => {
    try {
      const response = await opportunitiesAPI.getOpportunityById(id);
      return response.data.data as Opportunity;
    } catch (error) {
      console.error(`Error fetching opportunity with ID ${id}:`, error);
      throw error;
    }
  };

  const fetchOpportunityByStatusName = async (status: string, pagination: string) => {
    try {
      const response = await opportunitiesAPI.getOpportunityByStatus(status, pagination);
      return response.data.data as Opportunity[];
    } catch (error) {
      console.error(`Error fetching opportunity with status ${status}:`, error);
      throw error;
      return [];
    }
  };

  const createOpportunity = async (opportunityData: Partial<Opportunity>) => {
    try {
      const response = await opportunitiesAPI.createOpportunity(opportunityData);
      if (!response.data.success) {
        throw response.data;
      }
      opportunities.value.push(response.data.data as Opportunity);
      return response.data;
    } catch (error) {
      console.error('Error creating opportunity:', error);
      throw error;
    }
  };

  const updateOpportunity = async (id: number, opportunityData: Partial<Opportunity>) => {
    try {

      if (typeof opportunityData.additional_data === 'string') {
        try {
          opportunityData.additional_data = JSON.parse(opportunityData.additional_data);
        } catch (error) {
          console.warn('Invalid JSON in additional_data, ignoring.');
          opportunityData.additional_data = [];
          throw error;
        }
      }

      const response = await opportunitiesAPI.updateOpportunity(id, opportunityData);
      if (!response.data.success) {
        throw response.data;
      }
      const updated = response.data.data as Opportunity;
      const index = opportunities.value.findIndex((c) => c.id === id);
      if (index !== -1) {
        opportunities.value[index] = updated;
      }
      return response.data;
    } catch (error) {
      console.error(`Error updating opportunity with ID ${id}:`, error);
      throw error;
    }
  };

  const deleteOpportunity = async (id: number) => {
    try {
      await opportunitiesAPI.deleteOpportunity(id);
      opportunities.value = opportunities.value.filter((c) => c.id !== id);
    } catch (error) {
      console.error(`Error deleting opportunity with ID ${id}:`, error);
      throw error;
    }
  };

  const exportCSV = async (params: string, body: any) => {
    try {
      return await opportunitiesAPI.exportCSV(params, body);
    } catch (error) {
      console.error('Error exportando archivo:', error);
      throw error;
    }
  };

  return {
    opportunities,
    fetchOpportunities,
    fetchOpportunityById,
    createOpportunity,
    updateOpportunity,
    deleteOpportunity,
    fetchOpportunityByStatusName,
    exportCSV
  };
});
